import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CalendarClockIcon, CalendarDaysIcon, ClipboardCheckIcon, HashIcon, LandmarkIcon, Loader2Icon, ShieldCheckIcon, StickyNoteIcon, UmbrellaIcon, UsersIcon } from 'lucide-react';
import { LicenseData, MichiganLicenseData, StateComplianceData, User, US_STATES, formatCurrencyShort, getStateLicensedUserCount, hasCompanyStateCompliance } from './types';
type CoverageStatus = 'licensed' | 'officers' | 'none';
interface CoverageMapProps {
  users: User[];
  licenses: Record<string, Record<string, LicenseData>>;
  michiganLicenses: Record<string, MichiganLicenseData>;
  stateCompliance: Record<string, StateComplianceData>;
  onStateClick: (stateName: string) => void;
}
interface StateShape {
  name: string;
  path: string;
}
interface Topology {
  transform?: {
    scale: [number, number];
    translate: [number, number];
  };
  arcs: number[][][];
  objects: Record<string, {
    geometries: TopoGeometry[];
  }>;
}
interface TopoGeometry {
  type: string;
  arcs: unknown;
  properties?: {
    name?: string;
  };
}
type Point = [number, number];
const TOPO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-albers-10m.json';
const STATE_NAME_SET = new Set(US_STATES.map(state => state.name));
const STATUS_STYLES: Record<CoverageStatus, {
  fill: string;
  stroke: string;
  label: string;
}> = {
  licensed: {
    fill: '#10b981',
    stroke: '#34d399',
    label: 'Company licensed'
  },
  officers: {
    fill: '#b45309',
    stroke: '#f59e0b',
    label: 'Officers only'
  },
  none: {
    fill: '#1e293b',
    stroke: '#334155',
    label: 'No coverage'
  }
};
export function CoverageMap({
  users,
  licenses,
  michiganLicenses,
  stateCompliance,
  onStateClick
}: CoverageMapProps) {
  const [shapes, setShapes] = useState<StateShape[] | null>(null);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [hovered, setHovered] = useState<{
    name: string;
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    setShapes(null);
    setHasError(false);
    fetch(TOPO_URL).then(response => {
      if (!response.ok) throw new Error('Unable to load map data');
      return response.json() as Promise<Topology>;
    }).then(topology => {
      if (cancelled) return;
      setShapes(buildStateShapes(topology));
    }).catch(() => {
      if (!cancelled) setHasError(true);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);
  const statusByState = useMemo(() => {
    const map: Record<string, CoverageStatus> = {};
    for (const state of US_STATES) {
      if (hasCompanyStateCompliance(stateCompliance[state.name])) {
        map[state.name] = 'licensed';
        continue;
      }
      const counts = getStateLicensedUserCount(state.name, users, licenses, michiganLicenses);
      map[state.name] = counts.licensed > 0 || counts.inProgress > 0 ? 'officers' : 'none';
    }
    return map;
  }, [stateCompliance, users, licenses, michiganLicenses]);
  const totals = useMemo(() => {
    let licensed = 0;
    let officers = 0;
    for (const state of US_STATES) {
      if (statusByState[state.name] === 'licensed') licensed++;else if (statusByState[state.name] === 'officers') officers++;
    }
    return {
      licensed,
      officers
    };
  }, [statusByState]);
  const hoveredDetails = useMemo(() => {
    if (!hovered) return null;
    const compliance = stateCompliance[hovered.name];
    const counts = getStateLicensedUserCount(hovered.name, users, licenses, michiganLicenses);
    return {
      status: statusByState[hovered.name] || 'none',
      compliance,
      counts
    };
  }, [hovered, stateCompliance, statusByState, users, licenses, michiganLicenses]);
  return <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-xl border border-slate-800/80 bg-slate-900/30 shadow-2xl" data-id="element-127" data-mp="2f580eqm9xa89">
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-700/50 px-4 py-2.5" data-id="element-128" data-mp="goz94ad1wjzi">
        <div className="flex flex-col gap-0.5" data-id="element-129" data-mp="ugniw6ztuwdb">
          <h2 className="text-sm font-bold uppercase tracking-wider text-white" data-id="element-130" data-mp="uigqppm6fmmw">
            Company Coverage Map
          </h2>
          <p className="text-[10px] text-slate-500" data-id="element-131" data-mp="14ebeez59c7wj">
            Select a state to view or edit its company license details
          </p>
        </div>

        <div className="flex items-center gap-2" data-id="element-132" data-mp="zeyqsc6kawsw">
          <div className="flex items-center gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5" data-id="element-133" data-mp="1dezwq61oouf8">
            <ShieldCheckIcon className="h-3.5 w-3.5 text-emerald-400" data-id="element-134" data-mp="18h82349nmu7c" />
            <span className="text-xs text-slate-300" data-id="element-135" data-mp="3f6cvsegz083d">
              <span className="text-sm font-bold tabular-nums text-white" data-id="element-136" data-mp="3f6v2k519e4pj">
                {totals.licensed}
              </span>{' '}
              licensed
            </span>
          </div>
          {totals.officers > 0 && <div className="flex items-center gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-1.5" data-id="element-137" data-mp="1uokjecfrkenu">
              <UsersIcon className="h-3.5 w-3.5 text-amber-400" data-id="element-138" data-mp="1gxp6mp259dez" />
              <span className="text-xs text-slate-300" data-id="element-139" data-mp="1gvcrskronj65">
                <span className="text-sm font-bold tabular-nums text-white" data-id="element-140" data-mp="1kxpnegcpu0p7">
                  {totals.officers}
                </span>{' '}
                officers only
              </span>
            </div>}
        </div>
      </div>

      <div className="custom-scrollbar relative flex-1 min-h-0 overflow-auto bg-slate-900/50 p-4" onMouseMove={event => {
      setHovered(prev => prev ? {
        ...prev,
        x: event.clientX,
        y: event.clientY
      } : prev);
    }} data-id="element-141" data-mp="1bvq39f6emgi1">
        {!shapes && !hasError && <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 text-slate-400" data-id="element-142" data-mp="2wdxtgrrcvu5m">
            <Loader2Icon className="h-6 w-6 animate-spin text-emerald-400" data-id="element-143" data-mp="9600lndpxz2u" />
            <p className="text-sm" data-id="element-144" data-mp="46dnddxw4iu1">Loading coverage map…</p>
          </div>}

        {hasError && <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 text-center" data-id="element-145" data-mp="4imgajvg7gs">
            <AlertCircleIcon className="h-6 w-6 text-red-400" data-id="element-146" data-mp="3rbmrx10dbln3" />
            <div data-id="element-147" data-mp="3rd668m2grmm8">
              <p className="text-sm text-slate-300" data-id="element-148" data-mp="rvyggpnrcwh1">
                We couldn't load the coverage map.
              </p>
              <p className="mt-1 text-xs text-slate-500" data-id="element-149" data-mp="nu4kuvsf6inq">
                Check your connection and try again.
              </p>
            </div>
            <button onClick={() => setReloadKey(key => key + 1)} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400" data-id="element-150" data-mp="j1tilma7iks0">
              Retry
            </button>
          </div>}

        {shapes && <svg viewBox="0 0 975 610" className="mx-auto h-auto w-full max-w-4xl" role="img" aria-label={`Company license coverage map. ${totals.licensed} states licensed.`} data-id="element-151" data-mp="1e5y7wz820fw0">
            {shapes.map((shape, index) => {
          const status = statusByState[shape.name] || 'none';
          const style = STATUS_STYLES[status];
          return <motion.path key={shape.name} d={shape.path} tabIndex={0} role="button" aria-label={`${shape.name} — ${style.label}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.25,
            delay: Math.min(index, 40) * 0.008
          }} onMouseEnter={event => setHovered({
            name: shape.name,
            x: event.clientX,
            y: event.clientY
          })} onMouseLeave={() => setHovered(null)} onFocus={() => setHovered(null)} onClick={() => onStateClick(shape.name)} onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onStateClick(shape.name);
            }
          }} style={{
            fill: style.fill,
            fillOpacity: status === 'none' ? 0.85 : 0.75,
            stroke: style.stroke,
            strokeWidth: 0.75,
            strokeLinejoin: 'round',
            cursor: 'pointer',
            outline: 'none'
          }} className="transition-[fill-opacity] duration-150 hover:[fill-opacity:1] focus-visible:[fill-opacity:1]" data-id="element-152" data-mp="1x156sbuzwsfx" data-mpi={index} />;
        })}
          </svg>}

        {hovered && hoveredDetails && <div className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full pb-3" style={{
        left: hovered.x,
        top: hovered.y
      }} data-id="element-153" data-mp="jww6nohzf10x">
            <div className="min-w-[200px] rounded-xl border border-slate-700/60 bg-slate-900/95 px-3.5 py-3 shadow-2xl" data-id="element-154" data-mp="a6kq92h42zd5">
              <p className="text-sm font-bold text-white" data-id="element-155" data-mp="xvnit5daacmf">{hovered.name}</p>
              <span className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${hoveredDetails.status === 'licensed' ? 'bg-emerald-500/15 text-emerald-300' : hoveredDetails.status === 'officers' ? 'bg-amber-500/15 text-amber-300' : 'bg-slate-800 text-slate-400'}`} data-id="element-156" data-mp="12v8ff94ze8vz">
                {STATUS_STYLES[hoveredDetails.status].label}
              </span>

              {hoveredDetails.compliance && <div className="mt-2.5 space-y-1.5 border-t border-slate-800 pt-2.5" data-id="element-157" data-mp="7qhf78pnuu7u">
                  {hoveredDetails.compliance.renewalDate && <TooltipRow icon={CalendarClockIcon} label="Renewal" value={hoveredDetails.compliance.renewalDate} data-id="element-158" data-mp="3q3ozamktgzzs" />}
                  {hoveredDetails.compliance.suretyBondAmount != null && <TooltipRow icon={ShieldCheckIcon} label="Bond" value={`$${hoveredDetails.compliance.suretyBondAmount.toLocaleString()}${hoveredDetails.compliance.suretyBondHeldBy ? ` · ${hoveredDetails.compliance.suretyBondHeldBy}` : ''}`} data-id="element-159" data-mp="3l66oeqn8oap5" />}
                  {hoveredDetails.compliance.suretyCoverageAmount != null && <TooltipRow icon={UmbrellaIcon} label="Coverage" value={formatCurrencyShort(hoveredDetails.compliance.suretyCoverageAmount)} data-id="element-160" data-mp="2tviyjacdx85x" />}
                  {hoveredDetails.compliance.bondExpirationDate && <TooltipRow icon={CalendarDaysIcon} label="Bond expires" value={hoveredDetails.compliance.bondExpirationDate} data-id="element-161" data-mp="3dkb9t4fbe8l1" />}
                  {hoveredDetails.compliance.bondCode && <TooltipRow icon={HashIcon} label="Bond code" value={hoveredDetails.compliance.bondCode} data-id="element-162" data-mp="3hnzhro7uz66t" />}
                  {hoveredDetails.compliance.secretaryOfStateRenewalDate && <TooltipRow icon={LandmarkIcon} label="Sec. of State" value={hoveredDetails.compliance.secretaryOfStateRenewalDate} data-id="element-163" data-mp="3nd95izvwrg9i" />}
                  {hoveredDetails.compliance.auditDate && <TooltipRow icon={ClipboardCheckIcon} label="Audit" value={hoveredDetails.compliance.auditDate} data-id="element-164" data-mp="57mp4hasre41" />}
                </div>}

              {hoveredDetails.compliance?.notes && <div className="mt-2.5 flex gap-2 border-t border-slate-800 pt-2.5" data-id="element-165" data-mp="jx2djqmuv7gk">
                  <StickyNoteIcon className="mt-0.5 h-3 w-3 flex-shrink-0 text-slate-500" data-id="element-166" data-mp="otiy6auuj3ow" />
                  <p className="max-h-12 overflow-hidden text-[11px] leading-4 text-slate-300" data-id="element-167" data-mp="sx8n51ffnnag">
                    {hoveredDetails.compliance.notes}
                  </p>
                </div>}

              <div className="mt-2.5 flex items-center gap-3 border-t border-slate-800 pt-2.5 text-[11px] text-slate-400" data-id="element-168" data-mp="2ie2t0wncit5k">
                <span className="tabular-nums" data-id="element-169" data-mp="2dg1acctu0vum">
                  <span className="font-bold text-emerald-400" data-id="element-170" data-mp="29d4ma6liw7ja">
                    {hoveredDetails.counts.licensed}
                  </span>{' '}
                  licensed
                </span>
                <span className="tabular-nums" data-id="element-171" data-mp="3127tnv8phy05">
                  <span className="font-bold text-amber-400" data-id="element-172" data-mp="3153ovbt668kw">
                    {hoveredDetails.counts.inProgress}
                  </span>{' '}
                  in progress
                </span>
              </div>
            </div>
          </div>}
      </div>

      <div className="flex flex-shrink-0 flex-wrap items-center justify-center gap-5 border-t border-slate-700/50 bg-slate-900/60 px-4 py-2.5" data-id="element-173" data-mp="2nbcpgw95k52w">
        {(['licensed', 'officers', 'none'] as CoverageStatus[]).map(status => <div key={status} className="flex items-center gap-2" data-id="element-174" data-mp="3fyp1wdllmwih">
            <span className="h-2.5 w-2.5 rounded-full" style={{
          backgroundColor: STATUS_STYLES[status].fill,
          border: `1px solid ${STATUS_STYLES[status].stroke}`
        }} data-id="element-175" data-mp="2zjhb6t7y3s15" />
            <span className="text-xs text-slate-400" data-id="element-176" data-mp="2vj5pdd3ujlo3">
              {STATUS_STYLES[status].label}
            </span>
          </div>)}
      </div>
    </div>;
}
function TooltipRow({
  icon: Icon,
  label,
  value
}: {
  icon: typeof CalendarClockIcon;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-2 text-[11px]" data-id="element-177" data-mp="39jqc2kcvu1mv">
      <Icon className="h-3 w-3 flex-shrink-0 text-slate-500" data-id="element-178" data-mp="39h43qii63i3l" />
      <span className="text-slate-500" data-id="element-179" data-mp="3eh09am6ijhl9">{label}</span>
      <span className="ml-auto font-medium text-slate-200" data-id="element-180" data-mp="62hxc9dgbhwd">{value}</span>
    </div>;
}

// --- TopoJSON helpers (pre-projected AlbersUSA, 975x610 viewBox) ---

function buildStateShapes(topology: Topology): StateShape[] {
  const arcs = decodeArcs(topology);
  const collection = topology.objects.states;
  if (!collection) return [];
  const shapes: StateShape[] = [];
  for (const geometry of collection.geometries) {
    const name = geometry.properties?.name;
    if (!name || !STATE_NAME_SET.has(name)) continue;
    const path = geometryToPath(geometry, arcs);
    if (path) shapes.push({
      name,
      path
    });
  }
  return shapes.sort((a, b) => a.name.localeCompare(b.name));
}
function decodeArcs(topology: Topology): Point[][] {
  const {
    transform,
    arcs
  } = topology;
  const [scaleX, scaleY] = transform?.scale ?? [1, 1];
  const [translateX, translateY] = transform?.translate ?? [0, 0];
  return arcs.map(arc => {
    let x = 0;
    let y = 0;
    return arc.map(([deltaX, deltaY]) => {
      if (!transform) return [deltaX, deltaY] as Point;
      x += deltaX;
      y += deltaY;
      return [x * scaleX + translateX, y * scaleY + translateY] as Point;
    });
  });
}
function geometryToPath(geometry: TopoGeometry, arcs: Point[][]): string {
  const polygons: number[][][] = geometry.type === 'Polygon' ? [geometry.arcs as number[][]] : geometry.type === 'MultiPolygon' ? geometry.arcs as number[][][] : [];
  let path = '';
  for (const polygon of polygons) {
    for (const ring of polygon) {
      const points = ringToPoints(ring, arcs);
      if (points.length === 0) continue;
      path += `M${points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join('L')}Z`;
    }
  }
  return path;
}
function ringToPoints(ring: number[], arcs: Point[][]): Point[] {
  const points: Point[] = [];
  for (const index of ring) {
    const source = index < 0 ? [...arcs[~index]].reverse() : arcs[index];
    if (!source) continue;
    for (let i = 0; i < source.length; i++) {
      if (i === 0 && points.length > 0) continue;
      points.push(source[i]);
    }
  }
  return points;
}